import { Navigate, useLocation } from 'react-router-dom';
import { useExternalAuth } from '../../services/externalAuthContext';
import { useAuth } from '../../services/authContext';
import { LoadingPage } from '../common/Spinner';

interface ExternalRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard for the /external/* authenticated surface.
 *
 * Behavior (mirrors ROADMAP Phase 15 SC-1):
 *   - isLoading (either context) => render <LoadingPage />.
 *   - External user authenticated => render children.
 *   - Not external-authed AND internal-authed => redirect to /admin (cross-context UX nicety).
 *   - Not external-authed AND not internal-authed => redirect to /external/login,
 *     preserving location for ?from= bounceback.
 *
 * NOTE: This is the only place under /external/* allowed to import the internal useAuth.
 * Reading both contexts here lets us disambiguate "logged in elsewhere" from "logged out
 * entirely" (Landmine #8 in 15-RESEARCH.md). Pages under /external/* MUST NOT import useAuth.
 */
export function ExternalRoute({ children }: ExternalRouteProps) {
  const ext = useExternalAuth();
  const internal = useAuth();
  const location = useLocation();

  if (ext.isLoading || internal.isLoading) {
    return <LoadingPage />;
  }

  if (ext.isAuthenticated) {
    return <>{children}</>;
  }

  if (internal.isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/external/login" state={{ from: location }} replace />;
}
